/** 批量实验矩阵页（F-014）。参考 v0 设计布局。 */
import { useRef, useState } from 'react';
import { FlaskConical, Play, X, CheckCircle2, XCircle } from 'lucide-react';
import { BUILTIN_STRATEGIES, strategyLabel } from '../../strategy/registry';
import { TEMPLATES, sceneLabel } from '../../data/templates';
import { buildExperimentConfig, useSetup } from '../store';
import { runBatch, type BatchProgress, type BatchScene } from '../../session/batch-runner';
import { useResults } from '../results-store';
import { resultsToCsv } from '../../data/export-csv';
import { downloadText } from '../../data/download';
import type { ExperimentResult } from '../../data/export-csv';
import { Section } from '../primitives';
import { cn } from '../cn';

function toggle(list: string[], id: string): string[] {
  return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
}

function CheckGroup({ title, items, selected, onToggle, getLabel }: { title: string; items: { id: string; label: string }[]; selected: string[]; onToggle: (id: string) => void; getLabel?: (id: string) => string }) {
  return (
    <div className="flex flex-col gap-2">
      <p className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">{title}</p>
      <div className="flex flex-wrap gap-2">
        {items.map((it) => {
          const active = selected.includes(it.id);
          return (
            <button key={it.id} onClick={() => onToggle(it.id)} className={cn('h-7 px-3 text-xs rounded border transition-colors', active ? 'bg-primary/15 border-primary/50 text-primary' : 'bg-secondary/40 border-border text-muted-foreground hover:text-foreground hover:border-border/70')}>
              {getLabel ? getLabel(it.id) : it.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function BatchPage() {
  const rules = useSetup((s) => s.rules);
  const { lastBatch, setLastBatch, addToCompare } = useResults();
  const [strategies, setStrategies] = useState<string[]>(['nearest-car', 'cost-group']);
  const [seedsText, setSeedsText] = useState('1, 2, 3');
  const [scenes, setScenes] = useState<string[]>(['office-all-day']);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<BatchProgress | null>(null);
  const cancelRef = useRef(false);

  const strategyItems = [...BUILTIN_STRATEGIES.map((s) => ({ id: s.id, label: s.name })), { id: 'rule-engine', label: '自定义规则引擎' }];

  const run = async () => {
    const seeds = seedsText.split(',').map((x) => Number(x.trim())).filter((x) => Number.isFinite(x));
    const sceneList: BatchScene[] = TEMPLATES.filter((t) => scenes.includes(t.id)).map((t) => ({ id: t.id, config: buildExperimentConfig(t.setup) }));
    if (strategies.length === 0 || seeds.length === 0 || sceneList.length === 0) return;
    setRunning(true);
    cancelRef.current = false;
    setLastBatch([]);
    const rows = await runBatch({ strategies, seeds, scenes: sceneList, rules }, (p) => setProgress(p), () => cancelRef.current);
    setLastBatch(rows);
    setRunning(false);
    setProgress(null);
  };

  const exportCsv = () => { if (lastBatch.length) downloadText('batch.csv', resultsToCsv(lastBatch), 'text/csv'); };

  return (
    <div className="flex flex-col gap-5 max-w-5xl mx-auto">
      <Section title="实验矩阵（策略 × 种子 × 场景）" icon={FlaskConical}>
        <div className="flex flex-col gap-4">
          <CheckGroup title="策略" items={strategyItems} selected={strategies} onToggle={(id) => setStrategies((l) => toggle(l, id))} />
          <div className="flex flex-col gap-2">
            <p className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">种子（逗号分隔）</p>
            <input value={seedsText} onChange={(e) => setSeedsText(e.target.value)} placeholder="1, 2, 3" className="w-48 h-8 bg-input/40 border border-border rounded px-2.5 text-sm num text-foreground focus:outline-none focus:ring-1 focus:ring-primary/60" />
          </div>
          <CheckGroup title="场景（模板）" items={TEMPLATES.map((t) => ({ id: t.id, label: t.name }))} selected={scenes} onToggle={(id) => setScenes((l) => toggle(l, id))} />
        </div>
      </Section>

      <div className="flex items-center gap-3">
        {!running ? (
          <button onClick={run} className="flex items-center gap-2 h-9 px-5 rounded text-sm font-semibold bg-primary text-primary-foreground hover:bg-primary/85 transition-all shadow-[0_0_16px_oklch(0.72_0.165_196/20%)]"><Play className="w-4 h-4" />运行批量</button>
        ) : (
          <button onClick={() => { cancelRef.current = true; }} className="flex items-center gap-2 h-9 px-4 rounded text-sm font-semibold bg-destructive text-destructive-foreground hover:bg-destructive/85 transition-colors"><X className="w-4 h-4" />取消</button>
        )}
        {progress && (
          <div className="flex-1">
            <div className="h-2 bg-secondary rounded-full overflow-hidden"><div className="h-full bg-primary transition-all" style={{ width: `${(progress.done / progress.total) * 100}%` }} /></div>
            <div className="text-xs font-mono text-muted-foreground mt-1">{progress.done}/{progress.total} · {progress.lastLabel}</div>
          </div>
        )}
      </div>

      {lastBatch.length > 0 && (
        <Section title={`结果（${lastBatch.length} 组）`}>
          <div className="flex items-center gap-3 mb-3">
            <button onClick={() => addToCompare(lastBatch)} className="text-xs text-primary hover:text-primary/80">全部加入对比</button>
            <button onClick={exportCsv} className="text-xs text-primary hover:text-primary/80">导出 CSV</button>
            <a href="#/compare" className="ml-auto text-xs text-muted-foreground hover:text-foreground">查看对比 →</a>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-muted-foreground text-[11px] font-mono uppercase tracking-wider">
                <tr className="border-b border-border">
                  <th className="text-left py-1.5 px-2">场景</th><th className="text-left py-1.5 px-2">策略</th><th className="text-right py-1.5 px-2">种子</th>
                  <th className="text-center py-1.5 px-2">状态</th><th className="text-right py-1.5 px-2">送达</th><th className="text-right py-1.5 px-2">平均等待(s)</th>
                  <th className="text-right py-1.5 px-2">吞吐(/h)</th><th className="text-right py-1.5 px-2">利用率</th><th className="text-right py-1.5 px-2">耗时(ms)</th><th />
                </tr>
              </thead>
              <tbody className="text-foreground num">
                {lastBatch.map((r: ExperimentResult) => (
                  <tr key={r.id} className="border-b border-border/50">
                    <td className="py-1.5 px-2">{sceneLabel(r.sceneId)}</td>
                    <td className="py-1.5 px-2">{strategyLabel(r.strategyId)}</td>
                    <td className="text-right py-1.5 px-2">{r.seed}</td>
                    <td className="text-center py-1.5 px-2">{r.ok ? <CheckCircle2 className="w-3.5 h-3.5 text-chart-3 inline" /> : <XCircle className="w-3.5 h-3.5 text-destructive inline" />}</td>
                    <td className="text-right py-1.5 px-2">{r.ok ? r.summary.deliveredCount : '—'}</td>
                    <td className="text-right py-1.5 px-2">{r.ok ? (r.summary.avgWaitMs / 1000).toFixed(1) : r.error ?? '—'}</td>
                    <td className="text-right py-1.5 px-2">{r.ok ? r.summary.throughputPerHour.toFixed(0) : '—'}</td>
                    <td className="text-right py-1.5 px-2">{r.ok ? `${(r.summary.carUtilization * 100).toFixed(0)}%` : '—'}</td>
                    <td className="text-right py-1.5 px-2">{r.durationMs ?? ''}</td>
                    <td className="text-right py-1.5 px-2"><button onClick={() => addToCompare([r])} className="text-xs text-primary hover:text-primary/80">对比</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}
    </div>
  );
}
